<template>
    <div style="height: 100%; overflow: auto">
        <div v-if="currentUserMeta">
            <div class="panel m-5" style="background-color: white">
                <div
                    class="
                        panel-heading
                        has-darker-grey-color
                        is-flex
                        is-align-items-center
                        is-justify-content-space-between
                    "
                >
                    <p>Profil</p>
                    <b-button
                        icon-left="download"
                        type="is-primary"
                        @click="printData"
                    >
                        Herunterladen
                    </b-button>
                </div>
                <div class="container pt-3 pb-3">
                    <div class="stammdaten">
                        <div class="stammdaten-avatar-block">
                            <b-upload
                                v-model="newProfilePicture"
                                accept="image/*"
                                style="cursor: pointer"
                            >
                                <b-image
                                    class="profileImage stammdaten-avatar"
                                    :src="getPicture"
                                    alt="Profilbild"
                                    rounded
                                ></b-image>
                            </b-upload>
                            <div class="avatar-actions">
                                <button
                                    v-if="
                                        newProfilePicture || userProfilePicture
                                    "
                                    class="delete is-small"
                                    type="button"
                                    @click="removePicture()"
                                ></button>
                                <b-button
                                    v-if="pictureChanged"
                                    type="is-primary"
                                    size="is-small"
                                    @click="saveNewUserPicture()"
                                >
                                    Bild speichern
                                </b-button>
                            </div>
                        </div>
                        <div class="stammdaten-facts">
                            <p class="title is-5 mb-2">{{ fullName }}</p>
                            <div class="facts-columns">
                                <div>
                                    <p v-if="email">
                                        <a :href="'mailto:' + email">
                                            {{ email }}
                                        </a>
                                    </p>
                                    <p v-if="phone1Line">
                                        <a :href="'tel:' + phone1">
                                            {{ phone1Line }}
                                        </a>
                                    </p>
                                    <p v-if="phone2Line">
                                        <a :href="'tel:' + phone2">
                                            {{ phone2Line }}
                                        </a>
                                    </p>
                                    <p v-if="addressLine">
                                        {{ addressLine }}
                                    </p>
                                </div>
                                <div v-if="groups.length > 0">
                                    <p class="has-text-weight-bold">Gruppen</p>
                                    <p v-for="group in groups" :key="group.id">
                                        {{ group.attributes.group_name }}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="panel m-5 pb-2" style="background-color: white">
                <p class="panel-heading has-darker-grey-color">
                    Passwort
                </p>
                <div class="password-fields px-2 pt-2">
                    <b-field
                        label="Neues Passwort"
                        :message="
                            showPasswordErrors && $v.newPassword.$invalid
                                ? 'Passwort ist nicht lang genug!'
                                : ''
                        "
                        :type="
                            !showPasswordErrors
                                ? ''
                                : !$v.newPassword.$invalid
                                    ? 'is-success'
                                    : 'is-danger'
                        "
                    >
                        <b-input
                            v-model="newPassword"
                            v-on:input="$v.newPassword.$touch"
                            password-reveal
                            type="password"
                            autocomplete="off"
                        ></b-input>
                    </b-field>
                    <b-field
                        label="Neues Passwort wiederholen"
                        :message="
                            showPasswordErrors && $v.repeatNewPassword.$invalid
                                ? 'Passwort stimmt nicht überein!'
                                : ''
                        "
                        :type="
                            !showPasswordErrors
                                ? ''
                                : !$v.repeatNewPassword.$invalid &&
                                    !$v.newPassword.$invalid
                                    ? 'is-success'
                                    : 'is-danger'
                        "
                    >
                        <b-input
                            v-model="repeatNewPassword"
                            v-on:input="$v.repeatNewPassword.$touch"
                            password-reveal
                            type="password"
                            autocomplete="off"
                        ></b-input>
                    </b-field>
                    <b-button
                        class="password-save"
                        type="is-primary"
                        @click="saveNewPassword()"
                        v-bind:disabled="$v.$invalid"
                    >
                        Passwort speichern
                    </b-button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
/**
 * Module for the management of the current user.
 * @displayName Profile
 */

import { mapGetters } from "vuex";
import Parse from "parse/dist/parse.min.js";
import { v4 as uuid } from "uuid";
import { required, sameAs, minLength } from "vuelidate/lib/validators";
import { getModifiedImage, getUserPicture, MAX_PICTURE_SIZE }
    from "../../../lib/imageFunctions";
import { getPlzLocation, printUserPdf } from "@/lib/pdfPrinter/UserPdf";
export default {
    components: {},
    name: "ModuleSelection",
    data() {
        return {
            newPassword: "",
            repeatNewPassword: "",
            newProfilePicture: undefined,
            newProfilePictureModified: undefined,
        };
    },
    mounted() {
        this.$store.dispatch("setDeleteUserPicture", false);
    },
    updated() {},
    computed: {
        ...mapGetters({
            userProfilePicture: "usermetas/getUserProfilePicture",
            userFullName: "usermetas/getUserFullName",
            currentUserMeta: "usermetas/getCurrentUserMeta",
        }),
        fullName() {
            const a = this.currentUserMeta.attributes;
            return [ a.first_name, a.last_name ]
                .filter(Boolean).map(s => s.trim()).filter(Boolean).join(" ");
        },
        email() {
            const email = this.currentUserMeta.attributes.email;
            return email ? email.trim() : "";
        },
        phone1() {
            const phone = this.currentUserMeta.attributes.phone1;
            return phone ? phone.trim() : "";
        },
        phone2() {
            const phone = this.currentUserMeta.attributes.phone2;
            return phone ? phone.trim() : "";
        },
        phone1Line() {
            return this.formatPhone(
                this.phone1, this.currentUserMeta.attributes.info1);
        },
        phone2Line() {
            return this.formatPhone(
                this.phone2, this.currentUserMeta.attributes.info2);
        },
        addressLine() {
            const a = this.currentUserMeta.attributes;
            const street = a.street ? a.street.trim() : "";
            const city = getPlzLocation(a.plz, a.location);
            return [ street, city ]
                .filter(Boolean).map(s => s.trim()).filter(Boolean).join(", ");
        },
        groups() {
            return (this.currentUserMeta.attributes.groups || [])
                .filter(g => g && g.attributes);
        },
        pictureChanged() {
            return !!this.newProfilePicture;
        },
        // A pristine password form must not start out red; validate
        // only once either field has content.
        showPasswordErrors() {
            return this.newPassword.length > 0 ||
                this.repeatNewPassword.length > 0;
        },
        newProfilePictureUrl() {
            if (this.newProfilePicture) {
                return URL.createObjectURL(this.newProfilePicture);
            }
            return undefined;
        },
        getPicture() {
            if (!this.currentUserMeta) return undefined;
            const pictureObject = {
                newProfilePicture: this.newProfilePicture,
                userPicture: this.userProfilePicture,
                userGender: this.currentUserMeta.attributes.gender,
            };
            return getUserPicture(pictureObject);
        },
    },
    validations: {
        newPassword: { required, minLength: minLength(6) },
        repeatNewPassword: {
            sameAsPassword: sameAs("newPassword"),
        },
    },
    methods: {
        formatPhone(phone, info) {
            phone = phone ? phone.trim() : "";
            info = info ? info.trim() : "";
            if (!phone) return "";
            return info ? `${phone} (${info})` : phone;
        },
        printData() {
            printUserPdf(this.currentUserMeta);
        },
        removePicture() {
            // Only discards the not-yet-saved selection, no confirmation
            // needed for that.
            if (this.newProfilePicture) {
                this.newProfilePicture = undefined;
                return;
            }
            this.$buefy.dialog.confirm({
                cancelText: "Abbrechen",
                confirmText: "Entfernen",
                type: "is-danger",
                message: "Profilbild wirklich entfernen?",
                onConfirm: () => {
                    this.$store.dispatch("setDeleteUserPicture", true);
                    this.saveNewUserPicture();
                },
            });
        },
        async saveNewUserPicture() {
            const isRemoval = this.$store.state.deleteUserPicture &&
                !this.newProfilePicture;
            const UserMetaClass = Parse.Object.extend("UserMetaClass");
            const query = new Parse.Query(UserMetaClass);
            let usermeta = undefined;
            try {
                usermeta = await query.get(
                    Parse.User.current().attributes.dependency.id
                );
            } catch (e) {
                console.error("couldnt find the usermeta" + e);
            }
            if (usermeta) {
                if (
                    this.$store.state.deleteUserPicture &&
                    !this.newProfilePicture
                ) {
                    usermeta.unset("profile_picture");
                }
                if (this.newProfilePicture !== undefined) {
                    const imageFile = new Parse.File(
                        uuid(),
                        { base64: this.newProfilePictureModified },
                        "image/jpeg"
                    );
                    usermeta.set("profile_picture", imageFile);
                    await imageFile.save();
                }
                try {
                    await usermeta.save();
                } catch (e) {
                    console.error("couldnt save the usermeta " + e);
                }
            }
            await this.$store.dispatch("setDeleteUserPicture", false);
            this.newProfilePicture = undefined;
            this.newProfilePictureModified = undefined;
            this.$buefy.toast.open({
                message: isRemoval
                    ? "Profilbild entfernt!"
                    : "Neues Profilbild gespeichert!",
                type: "is-primary",
                position: "is-bottom",
            });
        },
        async saveNewPassword() {
            let currentUser = Parse.User.current();
            await currentUser.setPassword(this.newPassword);
            await currentUser.save();
            this.$buefy.toast.open({
                message: "Neues Passwort gespeichert!",
                type: "is-primary",
                position: "is-bottom",
            });
        },
    },
    watch: {
        newProfilePicture: {
            handler: function (fileArray) {
                if (fileArray) {
                    this.newProfilePictureModified = undefined;
                    if (fileArray.type.match(/image.*/)) {
                        getModifiedImage(fileArray, MAX_PICTURE_SIZE)
                            .then((response) => {
                                this.newProfilePictureModified = response;
                            })
                            .catch(function (error) {
                                console.error(error);
                            });
                    }
                }
            },
            deep: true,
        },
    },
};
</script>

<style>
.profileImage.image img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}
</style>
<style scoped>
.stammdaten {
    display: flex;
    align-items: flex-start;
    gap: 1.5rem;
    padding-left: 1.5rem;
}

.stammdaten-avatar-block {
    flex-shrink: 0;
}

.stammdaten-avatar {
    width: 8rem;
    height: 8rem;
}

.avatar-actions {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 0.5rem;
    margin-top: 0.5rem;
}

.stammdaten-facts {
    text-align: left;
    min-width: 0;
}

.stammdaten-facts p {
    margin-bottom: 0.25rem;
}

.facts-columns {
    display: flex;
    align-items: flex-start;
    flex-wrap: wrap;
    gap: 0.25rem 3.5rem;
}

.password-fields {
    display: flex;
    align-items: flex-start;
    flex-wrap: wrap;
    gap: 0.75rem;
    text-align: left;
}

.password-fields .field {
    flex: 1;
    min-width: 14rem;
    margin-bottom: 0;
}

/* Align the button with the inputs below their labels. */
.password-save {
    margin-top: 2em;
}

@media (max-width: 650px) {
    .panel.m-5 {
        margin: 0.75rem 0.5rem 1rem 0.5rem !important;
    }
}
</style>
